import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { DoorOpen, Clock, MapPin, Pencil, Check, X, Trash2, Users, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { OfficePopover, statusKey } from "./OfficePopover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (url) => (!url ? "" : url.startsWith("http") ? url : `${BACKEND_URL}${url}`);
const fmtMoney = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");
const fmtDate = (iso) => { try { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };

const STATUS = {
  available: { label: "Available", dot: "#16A34A", box: "rgba(34,197,94,0.62)", border: "#15803D", chip: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  notice: { label: "On notice", dot: "#EAB308", box: "rgba(234,179,8,0.62)", border: "#CA8A04", chip: "bg-amber-100 text-amber-700 border-amber-300" },
  signed: { label: "Signed (future)", dot: "#0284C7", box: "rgba(14,165,233,0.55)", border: "#0369A1", chip: "bg-sky-100 text-sky-800 border-sky-300" },
  occupied: { label: "Rented", dot: "#DC2626", box: "rgba(220,38,38,0.55)", border: "#B91C1C", chip: "bg-rose-100 text-rose-700 border-rose-300" },
};

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-brand-muted" data-testid="floorplan-legend">
      {["available", "signed", "notice", "occupied"].map((k) => {
        const s = STATUS[k];
        return (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="h-3.5 w-3.5 rounded-sm border" style={{ background: s.box, borderColor: s.border }} /> {s.label}
          </span>
        );
      })}
    </div>
  );
}

function OfficeCard({ o, testid, highlight }) {
  const s = STATUS[o.status] || STATUS.occupied;
  return (
    <div className={`bg-brand-surface rounded-md p-4 border ${highlight ? "border-brand-primary ring-2 ring-brand-primary/60" : "border-brand-border"}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-brand-ink font-medium">Office {o.office_number}</p>
          <p className="text-[11px] text-brand-tertiary mt-0.5">{o.desks ? `${o.desks} desk${o.desks > 1 ? "s" : ""}` : "—"}{o.window ? " · window" : ""}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border ${o.upcoming_occupant && o.status === "available" ? "bg-sky-100 text-sky-800 border-sky-300" : s.chip}`}>{o.upcoming_occupant && o.status === "available" ? "Signed" : s.label}</span>
      </div>
      {o.status === "notice" ? (
        <p className="text-[11px] mt-2 flex items-center gap-1" style={{ color: STATUS.notice.dot }}>
          <Clock className="h-3 w-3" /> {o.occupant ? `${o.occupant} · ` : ""}available {o.notice_date ? fmtDate(o.notice_date) : "soon"}
        </p>
      ) : o.status === "occupied" ? (
        <p className="text-[11px] mt-2 flex items-center gap-1" style={{ color: STATUS.occupied.dot }}>
          <Users className="h-3 w-3" /> {o.occupant ? `Rented by ${o.occupant}` : "Rented"}
        </p>
      ) : (
        <p className="text-[11px] mt-2 flex items-center gap-1" style={{ color: STATUS.available.dot }}>
          <DoorOpen className="h-3 w-3" /> {o.upcoming_occupant ? `Available until ${fmtDate(o.upcoming_move_in)}` : "Available now"}
        </p>
      )}
      {o.upcoming_occupant && (
        <p className="text-[11px] mt-1.5 rounded bg-sky-50 border border-sky-200 text-sky-800 px-2 py-1" data-testid={`office-signed-${o.office_number}`}>
          Signed — {o.upcoming_occupant} moves in {fmtDate(o.upcoming_move_in)}
        </p>
      )}
      <div className="mt-3 grid grid-cols-3 gap-1 text-center border-t border-brand-border pt-2">
        <div><p className="text-[9px] uppercase tracking-wide text-brand-tertiary">M2M</p><p className="font-mono text-sm text-brand-ink">{fmtMoney(o.monthly_price)}</p></div>
        <div><p className="text-[9px] uppercase tracking-wide text-brand-tertiary">6-mo</p><p className="font-mono text-sm text-brand-muted">{fmtMoney(o.price_6mo)}</p></div>
        <div><p className="text-[9px] uppercase tracking-wide text-brand-tertiary">12-mo</p><p className="font-mono text-sm text-brand-primary">{fmtMoney(o.price_12mo)}</p></div>
      </div>
    </div>
  );
}

function FloorPlanMapper({ location, fp, offices, isAdmin, onSaved, highlight }) {
  const officeByNum = useMemo(() => Object.fromEntries(offices.map((o) => [String(o.office_number), o])), [offices]);
  const [regions, setRegions] = useState(fp.regions || []);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null); // {x,y,w,h} while drawing
  const [dirty, setDirty] = useState(false);
  const [active, setActive] = useState(null); // clicked office (view mode)
  const imgWrap = useRef(null);
  useEffect(() => { setRegions(fp.regions || []); setDirty(false); }, [fp.regions]);

  const officeOptions = useMemo(() => offices.map((o) => String(o.office_number)).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })), [offices]);

  const frac = (e) => {
    const r = imgWrap.current.getBoundingClientRect();
    return { x: Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1), y: Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1) };
  };
  const onDown = (e) => { if (!editing) return; e.preventDefault(); const p = frac(e); setDraft({ x0: p.x, y0: p.y, x: p.x, y: p.y }); };
  const onMove = (e) => { if (!editing || !draft) return; const p = frac(e); setDraft((d) => ({ ...d, x: p.x, y: p.y })); };
  const onUp = () => {
    if (!editing || !draft) return;
    const x = Math.min(draft.x0, draft.x), y = Math.min(draft.y0, draft.y);
    const w = Math.abs(draft.x - draft.x0), h = Math.abs(draft.y - draft.y0);
    setDraft(null);
    if (w < 0.01 || h < 0.01) return;
    setRegions((rs) => [...rs, { office_number: "", x, y, w, h }]);
    setDirty(true);
  };
  const setOffice = (i, num) => { setRegions((rs) => rs.map((r, idx) => idx === i ? { ...r, office_number: num } : r)); setDirty(true); };
  const removeRegion = (i) => { setRegions((rs) => rs.filter((_, idx) => idx !== i)); setDirty(true); };
  const save = async () => {
    try {
      const clean = regions.filter((r) => r.office_number);
      await api.put("/proposals/floorplan-regions", { location, media_id: fp.id, regions: clean });
      toast.success(`Saved ${clean.length} mapped offices`, { description: fp.caption || "Floor plan" });
      setDirty(false); setEditing(false); onSaved && onSaved();
    } catch (err) { toast.error("Save failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  if (fp.pdf && !fp.image_url) {
    return (
      <div className="bg-brand-surface border border-brand-border rounded-md p-5" data-testid={`fp-pdf-${fp.id}`}>
        <p className="text-sm text-brand-ink">{fp.caption || "Floor plan (PDF)"}</p>
        <a href={mediaUrl(fp.url)} target="_blank" rel="noreferrer" className="text-xs text-brand-primary mt-2 inline-block">Open floor plan PDF →</a>
      </div>
    );
  }
  const baseImg = mediaUrl(fp.image_url || fp.url);

  return (
    <div className="bg-brand-surface border border-brand-border rounded-md p-4" data-testid={`fp-${fp.id}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-brand-ink flex items-center gap-1.5"><MapPin className="h-4 w-4 text-brand-tertiary" /> {fp.caption || "Floor plan"}</p>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {editing && dirty && <button onClick={save} data-testid={`fp-save-${fp.id}`} className="inline-flex items-center gap-1 text-xs bg-brand-primary text-brand-page px-3 py-1.5 rounded"><Check className="h-3.5 w-3.5" /> Save map</button>}
            <button onClick={() => { setEditing((v) => !v); setDraft(null); setActive(null); }} data-testid={`fp-edit-${fp.id}`}
              className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border ${editing ? "border-brand-primary text-brand-primary" : "border-brand-border text-brand-muted hover:text-brand-ink"}`}>
              <Pencil className="h-3.5 w-3.5" /> {editing ? "Done drawing" : "Edit map"}
            </button>
          </div>
        )}
      </div>
      {editing && <p className="text-[11px] text-brand-muted mb-2">Drag on the plan to draw a box over an office, then pick its number. Boxes color by live status once assigned.</p>}
      {!editing && <div className="mb-3"><Legend /></div>}
      <div ref={imgWrap} onClick={() => setActive(null)} className="relative w-full select-none" style={{ cursor: editing ? "crosshair" : "default" }}
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} data-testid={`fp-canvas-${fp.id}`}>
        <img src={baseImg} alt={fp.caption || "Floor plan"} className="w-full block rounded" draggable={false} />
        {regions.map((r, i) => {
          const o = officeByNum[String(r.office_number)];
          const st = o ? (STATUS[statusKey(o)] || STATUS.occupied) : { box: "rgba(169,97,71,0.35)", border: "#A96147" };
          const isHi = !editing && highlight && highlight.has(String(r.office_number));
          return (
            <div key={i} data-testid={`fp-region-${fp.id}-${i}`}
              onClick={(e) => { e.stopPropagation(); !editing && o && setActive(active?.i === i ? null : { o, r, i }); }}
              className={`absolute flex items-center justify-center text-[9px] font-semibold text-white transition-transform hover:scale-[1.04] ${isHi ? "z-10 animate-pulse" : ""} ${active?.i === i ? "z-20 ring-2 ring-brand-ink" : ""}`}
              style={{ left: `${r.x * 100}%`, top: `${r.y * 100}%`, width: `${r.w * 100}%`, height: `${r.h * 100}%`,
                background: st.box, border: isHi ? "2.5px solid #2c2c2c" : `1.5px solid ${st.border}`,
                boxShadow: isHi ? "0 0 0 3px rgba(44,44,44,0.35)" : "none",
                cursor: editing ? "default" : (o ? "pointer" : "default") }}>
              {editing ? (
                <div className="flex items-center gap-0.5 bg-white/90 rounded px-0.5 py-0.5" onMouseDown={(e) => e.stopPropagation()}>
                  <select value={r.office_number} onChange={(e) => setOffice(i, e.target.value)} data-testid={`fp-region-office-${fp.id}-${i}`}
                    className="text-[9px] text-brand-ink bg-transparent max-w-[64px]">
                    <option value="">office…</option>
                    {officeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <button onClick={() => removeRegion(i)} data-testid={`fp-region-del-${fp.id}-${i}`} className="text-brand-neg"><X className="h-3 w-3" /></button>
                </div>
              ) : (o ? `#${r.office_number}` : "")}
            </div>
          );
        })}
        {active && !editing && (
          <div className="absolute" style={{ left: `${(active.r.x + (active.r.x > 0.6 ? 0 : active.r.w)) * 100}%`, top: `${active.r.y * 100}%` }}>
            <OfficePopover o={active.o} location={location} onClose={() => setActive(null)}
              side={`${active.r.x > 0.6 ? "right-2" : "left-2"} ${active.r.y > 0.6 ? "bottom-0" : "top-0"}`} />
          </div>
        )}
        {draft && (
          <div className="absolute bg-brand-primary/20 border-2 border-brand-primary"
            style={{ left: `${Math.min(draft.x0, draft.x) * 100}%`, top: `${Math.min(draft.y0, draft.y) * 100}%`, width: `${Math.abs(draft.x - draft.x0) * 100}%`, height: `${Math.abs(draft.y - draft.y0) * 100}%` }} />
        )}
      </div>
    </div>
  );
}

export function AvailabilityPanel({ location, onSelectLocation, floorplanFirst = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/availability", { params: { location } }); setData(data); }
    catch (e) { setData({ summary: [], offices: [], floorplans: [] }); toast.error("Availability couldn't load", { description: e.userMessage, id: "availability" }); }
    finally { setLoading(false); }
  }, [location]);
  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || [];
  const totals = useMemo(() => summary.reduce((a, s) => ({ available: a.available + s.available, on_notice: a.on_notice + s.on_notice, occupied: a.occupied + s.occupied, total: a.total + s.total }), { available: 0, on_notice: 0, occupied: 0, total: 0 }), [summary]);
  const [query, setQuery] = useState("");
  const offices = data?.offices || [];
  const singleLoc = location !== "all";
  const availableOffices = offices.filter((o) => o.status === "available").sort((a, b) => !!a.upcoming_occupant - !!b.upcoming_occupant);
  const signedCount = availableOffices.filter((o) => o.upcoming_occupant).length;
  const noticeOffices = offices.filter((o) => o.status === "notice");
  const q = query.trim().toLowerCase();
  const matched = q ? offices.filter((o) => String(o.office_number).toLowerCase().includes(q) || (o.occupant || "").toLowerCase().includes(q)) : [];
  const highlightSet = new Set(matched.map((o) => String(o.office_number)));

  const setLocation = onSelectLocation || (() => {});
  const floorplans = (
            <div>
              <h2 className="text-lg text-brand-ink flex items-center gap-2 mb-3"><MapPin className="h-4 w-4 text-brand-tertiary" /> Floor plans</h2>
              {(data?.floorplans || []).length > 0 ? (
                <div className="space-y-4" data-testid="availability-floorplans">
                  {data.floorplans.map((fp) => (
                    <FloorPlanMapper key={fp.id} location={location} fp={fp} offices={offices} isAdmin={isAdmin} onSaved={load} highlight={highlightSet} />
                  ))}
                </div>
              ) : (
                <div className="bg-brand-surface border border-dashed border-brand-borderStrong rounded-md p-5 text-sm text-brand-muted" data-testid="no-floorplan">
                  No floor plan uploaded for {location}. Add one (image) in Proposals → Locations to enable the interactive availability map.
                </div>
              )}
            </div>
  );

  return (
    <div data-testid="availability-panel">
        {/* Totals */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="availability-totals">
          {[["Available", totals.available, STATUS.available.dot], ["On notice", totals.on_notice, STATUS.notice.dot], ["Rented", totals.occupied, STATUS.occupied.dot], ["Total offices", totals.total, "#2c2c2c"]].map(([label, val, dot]) => (
            <div key={label} className="bg-brand-surface border border-brand-border rounded-md p-4">
              <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: dot }} /> {label}</p>
              <p className="font-serif text-3xl text-brand-ink mt-1">{val}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>
        ) : !singleLoc ? (
          <div className="mt-8">
            {onSelectLocation && <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary mb-3">By location — select one to see offices & floor plan</p>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {summary.map((s) => (
                <button key={s.location} onClick={() => setLocation(s.location)} data-testid={`avail-loc-${s.location}`}
                  className="text-left bg-brand-surface border border-brand-border rounded-md p-4 hover:border-brand-primary transition-colors">
                  <p className="text-brand-ink font-medium">{s.location}</p>
                  <div className="flex gap-3 mt-2 text-sm">
                    <span className="text-brand-pos font-mono">{s.available} open</span>
                    <span className="text-brand-gold font-mono">{s.on_notice} notice</span>
                    <span className="text-brand-tertiary font-mono">{s.occupied} occ</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-8 space-y-8">
            {floorplanFirst && floorplans}
            {/* Search any office (number or company) */}
            <div>
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-tertiary" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="office-search"
                  placeholder="Search office # or who's in it…"
                  className="w-full border border-brand-border rounded-md pl-9 pr-8 py-2 text-sm bg-brand-surface text-brand-ink" />
                {query && <button onClick={() => setQuery("")} data-testid="office-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-tertiary hover:text-brand-ink"><X className="h-4 w-4" /></button>}
              </div>
              {q && (
                <div className="mt-4" data-testid="office-search-results">
                  <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary mb-2">{matched.length} result{matched.length === 1 ? "" : "s"} for “{query.trim()}”</p>
                  {matched.length === 0 ? (
                    <p className="text-sm text-brand-tertiary">No office or occupant matches that. Try just the office number.</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {matched.map((o) => <OfficeCard key={o.id} o={o} highlight testid={`search-office-${o.office_number}`} />)}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Available */}
            <div>
              <h2 className="text-lg text-brand-ink flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS.available.dot }} /> Available now <span className="text-sm text-brand-tertiary">({availableOffices.length}{signedCount ? ` · ${signedCount} already signed for a future move-in` : ""})</span></h2>
              {availableOffices.length === 0 ? <p className="text-sm text-brand-tertiary mt-2">No open offices right now.</p> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3" data-testid="available-list">
                  {availableOffices.map((o) => <OfficeCard key={o.id} o={o} highlight={highlightSet.has(String(o.office_number))} testid={`avail-office-${o.office_number}`} />)}
                </div>
              )}
            </div>

            {/* On notice */}
            <div>
              <h2 className="text-lg text-brand-ink flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS.notice.dot }} /> On notice — freeing soon <span className="text-sm text-brand-tertiary">({noticeOffices.length})</span></h2>
              {noticeOffices.length === 0 ? <p className="text-sm text-brand-tertiary mt-2">Nothing on notice.</p> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3" data-testid="notice-list">
                  {noticeOffices.sort((a, b) => (a.notice_date || "").localeCompare(b.notice_date || "")).map((o) => <OfficeCard key={o.id} o={o} highlight={highlightSet.has(String(o.office_number))} testid={`notice-office-${o.office_number}`} />)}
                </div>
              )}
            </div>

            {!floorplanFirst && floorplans}
          </div>
        )}
    </div>
  );
}

export function useLocationOptions() {
  const [locs, setLocs] = useState([]);
  useEffect(() => {
    api.get("/availability", { params: { location: "all" } })
      .then(({ data }) => setLocs((data.summary || []).map((s) => s.location))).catch(() => setLocs([]));
  }, []);
  return locs;
}
